import { Contact } from 'data/contact/contact.types';
import { FormTemplateOption } from 'data/template/template.types';
import decodeContactValue from './decodeContactValue';

/** First entry of the legal/network contact dropdowns: no contact selected. */
export const NONE_CONTACT_OPTION: FormTemplateOption = { label: 'None', value: '' };

/**
 * Builds the options for the legal/network contact dropdowns: 'None' followed by every
 * contact the system knows about (from /contacts).
 *
 * The contact name is both the option's label and its value: csp.legalcontact and
 * csp.nwcontact are plain strings on the wire (see XCspConfigDTO), and what they hold is
 * the contact's name itself, not its id - the backend derives a warrant's XIdentity id
 * from that name (see EWarrantServiceImpl.resolveWarrantContact) rather than the frontend
 * having to carry one.
 *
 * A CSP saved while these fields stored a contact id instead (or, further back, free text)
 * holds a value that will not match any contact by name - decodeContactValue reads that
 * back to a name for display, both here and in the CSP grid.
 *
 * currentValue is the value already stored on the CSP. It is kept as its own option when it
 * matches no contact, so the dropdown does not show blank and silently clear it on the next
 * save.
 */
export default function getContactOptions(
  contacts: Contact[],
  currentValue?: string,
): FormTemplateOption[] {
  const options: FormTemplateOption[] = [
    NONE_CONTACT_OPTION,
    ...contacts.map(({ name }) => ({ label: name, value: name })),
  ];

  if (currentValue && !options.some(({ value }) => value === currentValue)) {
    // Labelled with the decoded name, so a value from before this change reads as a name
    // rather than as a base64 id.
    options.push({ label: decodeContactValue(currentValue), value: currentValue });
  }

  return options;
}
